import { zodResolver } from '@hookform/resolvers/zod';
import { MarkEmailRead as MarkEmailReadIcon } from '@mui/icons-material';
import { Alert, Box, Tooltip, Typography } from '@mui/material';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { LoadingButton } from 'src/components/atoms/loading-button/loading-button';
import { ApiError } from 'src/types/interfaces/api-error';

import AuthFormHeader from '../../organisms/auth-form-header';
import AuthFormWrapper from '../auth-form-wrapper';

import { useForgotPasswordSchema } from './schema/forgot-password-schema';

import { useSendResetPasswordLink } from '@/features/auth/hooks/mutation/use-send-reset-password-link';
import { OrganizationLanguage } from '@/features/settings/api/types/gdpr';
import { useKeyDown } from '@/hooks/use-key-down';
import { Logo } from '@/icons/logo';

interface FormValues {
  email: string;
  password: string;
}

const defaultValues: FormValues = {
  email: '',
  password: '',
};

export const ForgotPassword = () => {
  const { t, i18n } = useTranslation(['validation', 'auth', 'common']);
  const navigate = useNavigate({ from: '/forgot-password' });
  const [isDisabledResendEmailButton, setIsDisabledResendEmailButton] = useState(false);

  const validationSchema = useForgotPasswordSchema();
  const expired = useSearch({ from: '/(auth)/forgot-password', select: search => search.expired });

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    setError,
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const email = watch('email');

  const { mutateAsync: sendResetPasswordLink, isSuccess: isSubmitSuccessful } =
    useSendResetPasswordLink();

  const onSubmit = async ({ email }: FormValues) => {
    try {
      await sendResetPasswordLink({
        email,
        language: i18n.language as OrganizationLanguage,
      });

      toast.success(t('auth:forgotPassword.resetPasswordToast'));

      setTimeout(() => {
        setIsDisabledResendEmailButton(false);
      }, 60 * 1000);

      setIsDisabledResendEmailButton(true);
    } catch (error) {
      const typedError = error as ApiError;
      setError('root', {
        message: typedError?.response?.data?.message ?? t('common:somethingWentWrong'),
      });
    }
  };

  useKeyDown('Enter', handleSubmit(onSubmit));

  useEffect(() => {
    if (expired) {
      setError('root', {
        message: t('auth:forgotPassword.linkExpired'),
      });
    }
  }, [expired]);

  return (
    <AuthFormWrapper>
      {isSubmitSuccessful ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}>
          <AuthFormHeader
            title={t('auth:forgotPassword.resetSuccessTitle')}
            description={t('auth:forgotPassword.resetSuccessContent', {
              email: email,
            })}
            icon={<MarkEmailReadIcon color="success" sx={{ width: '88px', height: '88px' }} />}
          />

          <Tooltip
            title={t('auth:forgotPassword.sendAgainTooltip')}
            disableHoverListener={!isDisabledResendEmailButton}>
            <span>
              <LoadingButton
                color="primary"
                variant="contained"
                disabled={isDisabledResendEmailButton}
                loading={isSubmitting}
                onClick={handleSubmit(onSubmit)}>
                {t('auth:forgotPassword.sendAgainButton')}
              </LoadingButton>
            </span>
          </Tooltip>
        </Box>
      ) : (
        <>
          <AuthFormHeader
            title={t('auth:forgotPassword.title')}
            description={t('auth:forgotPassword.content')}
            icon={<Logo />}
          />
          <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <InputField
                  label={t('auth:email')}
                  fullWidth
                  required
                  inputRef={ref}
                  onBlur={onBlur}
                  onChange={onChange}
                  type="email"
                  value={value}
                  autoComplete="email"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
            {errors?.root?.message ? <Alert severity="error">{errors?.root?.message}</Alert> : null}
            <Typography
              color="primary"
              variant="subtitle2"
              sx={{ cursor: 'pointer', color: '#6EBAD3' }}
              onClick={() => navigate({ to: '/login' })}>
              {t('auth:forgotPassword.redirectToLogin')}
            </Typography>

            <LoadingButton
              variant="contained"
              size="medium"
              disabled={!isValid}
              loading={isSubmitting}
              onClick={handleSubmit(onSubmit)}
              sx={{ my: 2, width: '100%' }}>
              {t('auth:forgotPassword.resetButton')}
            </LoadingButton>
          </Box>
        </>
      )}
    </AuthFormWrapper>
  );
};
